import { PlusIcon, ProhibitIcon, TrashIcon, WarningIcon } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { IterableElement } from 'type-fest';

import { Alert } from '../../../../components/alert/Alert';
import { Button } from '../../../../components/button/Button';
import { ConfirmDialog } from '../../../../components/dialog/ConfirmDialog';
import { ITableHeader, Table } from '../../../../components/table/Table';
import { deleteOrderLinePurchase, updateOrder } from '../../models';
import { useUniqueArrayReducer } from '../../../../hooks/useUniqueArrayReducer';
import { getDisplayError } from '../../../../utils/get-display-error';
import { decimateAndFormatNumber } from '../../../../utils/number';
import { calculateLineTotal } from '../../utils/price';
import type { Order } from '../order/Order';
import { useOrder } from '../order/orderContext';
import type { OrderLine } from '../orderLine/OrderLines';
import { useOrderLine } from '../orderLine/orderLineContext';
import { LinkButton } from '../../../../components/button/ButtonLink';
import { captureException } from '@sentry/react';
import { useAuth } from '../../../../contexts/auth-context';
import { useMinimalGeneralSettings } from '../../../../contexts/minimal-settings-context';
import {
  getEffectiveDieselSurchargePercentage,
  isDieselSurchargeProductType,
} from '../../../../domain/diesel-surcharge';
import { roleToNumber } from '../../../auth/roles.client';

export type OrderLinePurchase = IterableElement<IterableElement<Order['lines']>['purchases']>;

const ORDERLINE_HEADERS: ITableHeader[] = [
  {
    id: 'product',
    name: 'Artikel',
  },
  {
    id: 'amount',
    name: 'Hoeveelheid',
  },
  {
    id: 'unitPrice',
    name: 'Prijs',
  },
  {
    id: 'vatRate',
    name: 'Btw',
  },
  {
    id: 'total',
    name: 'Totaal',
  },
  {
    id: 'delete',
    name: '',
  },
];

export const OrderPurchasesPage = () => {
  const { orderLine, refreshData } = useOrderLine();
  const { order } = useOrder();

  if (order.noPurchase) {
    return <NoPurchaseNotice order={order} refreshData={refreshData} />;
  }

  return <PurchasesTable orderLine={orderLine} refreshData={refreshData} />;
};

const NoPurchaseNotice: React.FC<{ order: Order; refreshData: () => void }> = ({ order, refreshData }) => {
  const { me } = useAuth();
  const isPurchaseReadOnly = order.purchaseLocked && roleToNumber(me.role) < roleToNumber('InternalAdmin');
  const [isEnabling, setIsEnabling] = useState(false);

  const enablePurchases = async () => {
    setIsEnabling(true);
    try {
      await updateOrder(order.id, { noPurchase: false });
      toast.success('Aankopen weer ingeschakeld');
      await refreshData();
    } catch (err) {
      captureException(err);
      toast.error('Kon aankopen niet inschakelen: ' + getDisplayError(err));
    } finally {
      setIsEnabling(false);
    }
  };

  return (
    <Alert type="info" icon={<ProhibitIcon className="w-5 h-5" />} title="Aankopen zijn uitgeschakeld voor deze order">
      <div className="space-y-3">
        <p>
          Deze order is aangeduid met &laquo;Geen aankopen&raquo;, waardoor er geen aankooplijnen kunnen worden bekeken
          of toegevoegd. Dit gebeurt wanneer iemand het vinkje &laquo;Geen aankopen&raquo; heeft aangezet bij het
          aanmaken of aanpassen van de order.
        </p>
        <p>
          Wil je toch aankopen registreren op deze order? Schakel ze dan hieronder terug in. Dit zet het vinkje
          &laquo;Geen aankopen&raquo; weer uit; bestaande aankooplijnen worden hierbij niet gewist.
        </p>
        <p>
          Let op: als deze order al gekoppeld is aan een aankoopfactuur (bv. via een afgewerkt ritoverzicht of self
          billing), blijven nieuwe aankopen geblokkeerd, ook nadat je ze weer inschakelt.
        </p>
        <Button
          color="primary"
          onTrigger={enablePurchases}
          isDisabled={isEnabling || isPurchaseReadOnly}
          isLoading={isEnabling}
        >
          Aankopen weer inschakelen
        </Button>
      </div>
    </Alert>
  );
};

const PurchasesTable: React.FC<{ orderLine: OrderLine; refreshData: () => void }> = ({ orderLine, refreshData }) => {
  const { order } = useOrder();
  const { me } = useAuth();
  const { settings } = useMinimalGeneralSettings();
  const isPurchaseReadOnly = order.purchaseLocked && roleToNumber(me.role) < roleToNumber('InternalAdmin');
  const [, mutatePendingDeletion] = useUniqueArrayReducer<number>();

  const purchases: OrderLinePurchase[] = orderLine.purchases;

  const dieselSurchargePercentage = getEffectiveDieselSurchargePercentage(
    order.supplier,
    settings.dieselSurchargePercentage,
  );
  const hasDieselSurcharge = purchases.some((p) =>
    isDieselSurchargeProductType(p.productType.id, settings.dieselSurchargeProductType?.id),
  );

  const lines: Array<OrderLinePurchase | 'total'> = useMemo(() => {
    return [...purchases, 'total'];
  }, [purchases]);

  const total = useMemo(() => {
    return purchases.reduce((acc, item) => {
      return acc + calculateLineTotal(item);
    }, 0);
  }, [purchases]);

  return (
    <div>
      <div className="flex justify-end mb-4">
        {!isPurchaseReadOnly && (
          <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
            Voeg aankoop toe
          </LinkButton>
        )}
      </div>
      <Table
        idKey="id"
        headers={ORDERLINE_HEADERS}
        data={lines}
        mapData={(data) => {
          if (data === 'total') {
            return [
              <span className="font-medium">Totaal</span>,
              '',
              '',
              '',
              <span className="font-medium whitespace-nowrap">{`€ ${decimateAndFormatNumber(total, 2, {
                decimalSeperator: ',',
              })}`}</span>,
              '',
            ];
          } else {
            const deleteLine = async () => {
              mutatePendingDeletion({
                type: 'add',
                value: data.id,
              });
              try {
                await deleteOrderLinePurchase(data.id);
                toast.success('Order verkoop lijn gewist');
                await refreshData();
              } catch (err) {
                captureException(err);
                toast.error('Kon order verkoop lijn niet wissen: ' + getDisplayError(err));
              }
              mutatePendingDeletion({
                type: 'delete',
                value: data.id,
              });
            };

            return [
              <Link to={`${data.id}`} className="link-text">
                {data.productType.nameNl}
              </Link>,
              <div className="whitespace-nowrap">
                {`${decimateAndFormatNumber(data.amount, 2, {
                  decimalSeperator: ',',
                })}`}
              </div>,
              <div className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(data.unitPrice, 2, {
                decimalSeperator: ',',
              })}`}</div>,
              <div className="whitespace-nowrap">{`${decimateAndFormatNumber(data.vatRatePercentage, 2, {
                decimalSeperator: ',',
              })}%`}</div>,
              <div className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(calculateLineTotal(data), 2, {
                decimalSeperator: ',',
              })}`}</div>,
              isPurchaseReadOnly ? (
                ''
              ) : (
                <ConfirmDialog
                  triggerColor="danger"
                  triggerText={<TrashIcon className="button-icon" />}
                  title="Verwijder aankoop"
                  submitText="Verwijder"
                  description={<div>{`Ben je zeker dat je deze aankoop ${data.productType.nameNl} wilt wissen?`}</div>}
                  onSubmit={deleteLine}
                />
              ),
            ];
          }
        }}
        isMultiSelect
      />

      {dieselSurchargePercentage > 0 && !hasDieselSurcharge && (
        <div className="flex items-center gap-2 text-warning-text">
          <WarningIcon className="h-4 w-4" />
          <div>Diesel toeslag is nog niet toegekend.</div>
        </div>
      )}
    </div>
  );
};
